import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ArrowLeft, Lock, Play, CheckCircle2, Clock, AlertCircle,
  CalendarClock, Baby, Crown, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import EliteUpgradeCard from '../../components/EliteUpgradeCard';

/**
 * V13.14 Phase B — Learner Course Player.
 * Route: /learner/learn/course/:courseId
 *
 * Shows:
 *  - Course hero
 *  - Episode list with per-ep access state (lock reasons, countdowns, "notify me")
 *  - Embedded Bunny player when an unlocked episode is selected, with
 *    a subtle moving watermark overlay (Phase D preview).
 */
export default function LearnCoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // episode_id being played
  const [streamData, setStreamData] = useState(null);
  // V14.5 Part 19w — `purchasingTier` locks both buy buttons during the
  // OPS→Razorpay round-trip so a learner can't double-tap and create two
  // pending orders. Set to the tier_key currently in flight (e.g. "paid_trial").
  const [purchasingTier, setPurchasingTier] = useState(null);
  // V14.5 Part 17b — bumping the iframe `key` forces React to remount the
  // iframe with a fresh signed URL after a proactive token refresh OR after
  // we detect a 403 from Bunny mid-playback. The player rewinds to start;
  // that's acceptable for the rare TTL-overrun case (≈4h+ continuous viewing).
  const [iframeRefreshKey, setIframeRefreshKey] = useState(0);
  const progressRef = useRef({ last: 0, timer: null });
  const refreshTimerRef = useRef(null);

  const loadDetail = useCallback(async (opts = {}) => {
    // V14.7 — Don't show skeleton when a video is actively playing.
    // setLoading(true) unmounts the entire player area (iframe included),
    // causing the Bunny player to restart from 0:00.
    const silent = opts.silent || !!selected;
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/access/course/${courseId}/detail`);
      setDetail(res.data);
    } catch {
      if (!silent) toast.error('Could not load course');
    }
    if (!silent) setLoading(false);
  }, [courseId, selected]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // V14.5 Part 19w — initiate Razorpay checkout for a course tier.
  // Server (`/api/access/course/{id}/buy?tier_key=...`) creates an OPS
  // order; we open Razorpay inline; on success we verify via the existing
  // `/api/payment/verify` and reload the course detail (the OPS webhook
  // grants the entitlement). Purposefully mirrors the Dashboard.js trial
  // flow so the founder gets one consistent payment UX.
  const purchaseTier = useCallback(async (tierKey) => {
    if (purchasingTier) return;
    setPurchasingTier(tierKey);
    try {
      const orderRes = await api.post(
        `/api/access/course/${courseId}/buy?tier_key=${tierKey}`,
      );
      const { order_id, razorpay_key_id, amount, currency, course_title } = orderRes.data || {};
      if (!order_id || !razorpay_key_id) {
        toast.error('Could not start checkout. Please try again.');
        setPurchasingTier(null);
        return;
      }
      if (!window.Razorpay) {
        toast.error('Payment gateway is loading. Please retry in a few seconds.');
        setPurchasingTier(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: razorpay_key_id,
        amount,
        currency: currency || 'INR',
        name: 'Soul Sanskar Academy',
        description: course_title || 'Course purchase',
        order_id,
        prefill: {
          name: detail?.course?.title ? '' : '',  // server already has user info
        },
        theme: { color: '#4A6741' },
        handler: async (resp) => {
          try {
            const v = await api.post('/api/payment/verify', {
              order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            if (v.data?.verified) {
              toast.success('Payment successful! Unlocking the course…');
              // OPS webhook will land within a couple of seconds — poll the
              // detail endpoint a few times so the unlock appears without a
              // hard reload.
              for (let i = 0; i < 6; i += 1) {
                await new Promise((r) => setTimeout(r, 1500));
                await loadDetail();
              }
            } else {
              toast.error('Payment verification failed. Reach out if charged.');
            }
          } catch {
            toast.error('Verification error. Contact support if charged.');
          }
          setPurchasingTier(null);
        },
        modal: {
          ondismiss: () => setPurchasingTier(null),
        },
      });
      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail
                || 'Unable to start checkout';
      toast.error(typeof msg === 'string' ? msg : 'Unable to start checkout');
      setPurchasingTier(null);
    }
  }, [courseId, detail, loadDetail, purchasingTier]);

  const playEpisode = async (ep) => {
    setSelected(ep.id);
    setStreamData(null);
    // Smooth-scroll the player into view so it's obvious which episode is active.
    setTimeout(() => {
      const playerEl = document.getElementById('course-player-container');
      if (playerEl) playerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    try {
      const res = await api.post(
        `/api/access/course/${courseId}/episode/${ep.id}/stream-token`,
      );
      // Backend returns {status, message} when episode isn't ready (e.g., still
      // encoding or failed) — surface a clean state instead of letting Bunny's
      // generic "Processing video" placeholder load and confuse the learner.
      if (res.data?.status && !res.data?.embed_url) {
        setStreamData({ not_ready: true, status: res.data.status, message: res.data.message });
        return;
      }
      setStreamData(res.data);
      progressRef.current.last = 0;

      // V14.5 Part 17b — PROACTIVE TOKEN REFRESH (Bunny 403 mitigation)
      // Backend now issues a 4h-TTL signed URL (was 60s). But Bunny libraries
      // may ALSO enforce a per-library "View Token expiration" set in the
      // Bunny dashboard (default can be as low as 60-300s depending on plan).
      // Defence in depth: schedule a token refresh at ~50% of the TTL so the
      // iframe gets a fresh URL well before any internal session token can
      // expire. Refresh = remount iframe via `iframeRefreshKey` bump.
      // Trade-off: the player rewinds to start on refresh. With 4h base TTL,
      // refresh fires at 2h — well past any single-episode duration (<60m),
      // so the user only ever sees this if they're idle on the page for 2h+.
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      const ttlSec = res.data?.ttl_seconds || 14400;
      // Fire at 50% of TTL, clamped to [5min, 30min] for sensible bounds.
      const refreshAtMs = Math.max(5 * 60_000, Math.min(30 * 60_000, ttlSec * 1000 * 0.5));
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const fresh = await api.post(
            `/api/access/course/${courseId}/episode/${ep.id}/stream-token`,
          );
          if (fresh.data?.embed_url) {
            setStreamData(fresh.data);
            setIframeRefreshKey((k) => k + 1);
          }
        } catch { /* swallow — next user click will refresh */ }
      }, refreshAtMs);

      // Report progress every 30s as a watch heartbeat.
      // V14.7 — Fixed: the old 15s interval with +10% increments reached
      // 100% at 150s (2.5 min) regardless of actual video length, calling
      // loadDetail() which unmounted the player. Now the interval just sends
      // the heartbeat without faking completion. Actual completion is tracked
      // by the backend via the /progress endpoint's pct >= 95 check.
      if (progressRef.current.timer) clearInterval(progressRef.current.timer);
      let heartbeatCount = 0;
      progressRef.current.timer = setInterval(() => {
        heartbeatCount++;
        // Rough estimate: report increasing pct based on elapsed time vs a
        // conservative 90-min assumed max duration. Never auto-hit 100%.
        const elapsed = heartbeatCount * 30;
        const pct = Math.min(90, Math.round((elapsed / 5400) * 100));
        api.post(
          `/api/access/course/${courseId}/episode/${ep.id}/progress`,
          { pct, position_seconds: elapsed, duration_seconds: 0 },
        ).catch(() => { });
      }, 30000);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start playback';
      toast.error(msg);
      setSelected(null);
    }
  };

  // V14.5 Part 17b — manual recovery handler: learner taps "Reload player"
  // when they see the 403 fallback. Re-fetches a fresh token + remounts
  // the iframe in place. Preserves selected episode state.
  const manualReloadPlayer = async () => {
    const ep = episodes.find((e) => e.id === selected);
    if (!ep) return;
    setStreamData(null);
    try {
      const res = await api.post(
        `/api/access/course/${courseId}/episode/${ep.id}/stream-token`,
      );
      if (res.data?.embed_url) {
        setStreamData(res.data);
        setIframeRefreshKey((k) => k + 1);
        toast.success('Player refreshed');
      }
    } catch {
      toast.error('Could not reload — please try playing the episode again');
    }
  };

  useEffect(() => () => {
    if (progressRef.current.timer) clearInterval(progressRef.current.timer);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="learn-course-player-loading">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }
  if (!detail) return <div className="p-8">Course not found.</div>;

  const { course, episodes = [], course_access = {} } = detail;
  const owned = course_access.owned || course_access.allowed;
  const selectedEp = selected ? episodes.find(e => e.id === selected) : null;
  const selectedIndex = selectedEp ? episodes.findIndex(e => e.id === selected) : -1;

  // V14.5 Part 19w — A tier is "purchasable" only if admin set BOTH an OPS
  // SKU and a price. Empty SKU or zero price = founder hasn't wired the
  // OPS contract yet; we route the learner to the Elite WhatsApp CTA
  // instead of letting them tap a button that 400s.
  const purchasableTiers = (course?.tiers_enabled || []).filter(
    (t) => (t.key === 'paid_trial' || t.key === 'full')
        && t.ops_sku
        && Number(t.price_inr) > 0,
  );
  const showEliteCta = !owned && purchasableTiers.length === 0;

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="learn-course-player">
      <button
        onClick={() => navigate('/learner/learn')}
        className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground"
        data-testid="back-to-learn-tab"
      >
        <ArrowLeft className="h-3 w-3" />Back to Learn
      </button>

      {/* Course header */}
      <Card className="overflow-hidden rounded-2xl border-rose-100">
        <div className="aspect-[16/5] bg-gradient-to-br from-rose-100 via-pink-100 to-purple-100 relative">
          {course.preview_image_url ? (
            <img
              src={course.preview_image_url}
              alt={course.title}
              className="w-full h-full object-cover"
            />
          ) : null}
          {course.marketing_badge && (
            <Badge className="absolute top-3 right-3 bg-white/90 text-rose-700 border-rose-200">
              {course.marketing_badge}
            </Badge>
          )}
        </div>
        <CardContent className="pt-5">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold">
            {course.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {course.description}
          </p>
          <div className="flex gap-2 mt-3 items-center">
            <Badge variant="outline" className="text-[11px]">
              {course.total_episodes} episodes
            </Badge>
            {course_access.tier && (
              <Badge className="text-[11px] bg-rose-500">
                <Crown className="h-3 w-3 mr-1" />{course_access.tier}
              </Badge>
            )}
            {!owned && (
              <Badge variant="outline" className="text-[11px] border-amber-300 text-amber-700">
                <Lock className="h-3 w-3 mr-1" />Locked
              </Badge>
            )}
          </div>
          {!owned && course_access.lock_message && (
            <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 p-3 text-xs text-rose-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-[2px] flex-shrink-0" />
              <span>{course_access.lock_message}</span>
            </div>
          )}

          {/* V14.5 Part 19w — Buy CTAs: shown when the course is locked AND
              at least one tier is fully wired (SKU + price). One button per
              tier; clicking opens Razorpay. */}
          {!owned && purchasableTiers.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="course-buy-ctas">
              {purchasableTiers.map((t) => (
                <Button
                  key={t.key}
                  onClick={() => purchaseTier(t.key)}
                  disabled={!!purchasingTier}
                  className="bg-rose-600 hover:bg-rose-700 text-white font-medium"
                  data-testid={`buy-tier-${t.key}`}
                >
                  {purchasingTier === t.key ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing…</>
                  ) : (
                    // V14.5 Part 19z6 — Founder bug (2026-05-21): admin
                    // updated `price_inr` from 199 → 1099 but learner saw
                    // stale "Buy for ₹999" because `t.cta` was a stored
                    // string from the seed. Always derive the price text
                    // from the SSOT (`price_inr`) when present so admin
                    // pricing changes always reflect to learners.
                    <>{t.price_inr ? (t.key === 'paid_trial' ? `Try for ₹${t.price_inr}` : `Buy for ₹${t.price_inr}`) : (t.cta || 'Buy')}</>
                  )}
                </Button>
              ))}
            </div>
          )}

          {/* V14.5 Part 19w — Elite Program CTA: shown only when nothing
              purchasable is configured. Replaces the legacy "This content
              isn't available for purchase right now." dead-end. */}
          {showEliteCta && (
            <div className="mt-4" data-testid="course-elite-cta-wrapper">
              <EliteUpgradeCard size="full" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* V14.5 — Player container with anchor + clear "Now playing" banner.
          Animated equalizer + episode index makes it impossible to miss
          which video is currently active. Also handles the "video isn't
          ready" state cleanly instead of letting Bunny's generic
          "Processing video" placeholder render and confuse the learner. */}
      <div id="course-player-container" className="scroll-mt-4">
        {selected && selectedEp && (
          <div className="rounded-xl bg-rose-50/80 border border-rose-200 px-4 py-2.5 mb-3 flex items-center gap-3" data-testid="now-playing-banner">
            <span className="flex items-end gap-[2px] h-4" aria-label="Now playing">
              <span className="w-[3px] bg-rose-600 rounded-full ss-eq-1" />
              <span className="w-[3px] bg-rose-600 rounded-full ss-eq-2" />
              <span className="w-[3px] bg-rose-600 rounded-full ss-eq-3" />
              <span className="w-[3px] bg-rose-600 rounded-full ss-eq-2" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">
                Now playing · Episode {selectedIndex + 1} of {episodes.length}
              </p>
              <p className="text-sm font-semibold text-rose-900 truncate">{selectedEp.title}</p>
            </div>
          </div>
        )}

      {/* Player (only when selected + stream ready) */}
      {selected && streamData && !streamData.not_ready && (
        <Card className="overflow-hidden rounded-2xl border-2 border-rose-200">
          <div className="relative bg-black aspect-video" data-testid="course-player-embed">
            <iframe
              key={iframeRefreshKey}
              title="Episode"
              src={streamData.embed_url}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
            {/* V14.5 Part 17b — escape hatch: if the iframe shows a Bunny
                403 mid-playback (rare, but possible if the library's view-
                token expiry is shorter than our 4h URL TTL), the learner
                can tap this to re-fetch a fresh token and remount the player.
                Sits over the iframe with low opacity so it doesn't distract
                during normal viewing — but is always reachable. */}
            <button
              type="button"
              onClick={manualReloadPlayer}
              className="absolute top-2 right-2 z-10 px-2 py-1 rounded-md bg-black/40 hover:bg-black/65 text-white text-[10px] font-medium backdrop-blur-sm transition-colors"
              title="If video shows 403 or won't play, tap to reload"
              data-testid="reload-player-btn"
            >
              ↻ Reload
            </button>
            {/* Watermark overlay (Phase D — Lissajous drift + opacity pulse;
                larger motion radius resists a single static crop.) */}
            {streamData.watermark && (
              <div
                className="absolute text-[11px] font-medium text-white pointer-events-none select-none"
                style={{
                  right: '6%', bottom: '8%',
                  textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 0 12px rgba(0,0,0,0.6)',
                  animation: 'ss-wm-drift 47s ease-in-out infinite, ss-wm-pulse 9s ease-in-out infinite',
                  letterSpacing: '0.02em',
                  willChange: 'transform, opacity',
                }}
                data-testid="player-watermark"
              >
                {streamData.watermark.text}
              </div>
            )}
            <style>{`
              @keyframes ss-wm-drift {
                0%   { transform: translate(0, 0); }
                12%  { transform: translate(-22px, -14px); }
                27%  { transform: translate(-38px, 6px); }
                41%  { transform: translate(-12px, 22px); }
                58%  { transform: translate(20px, 10px); }
                73%  { transform: translate(8px, -18px); }
                88%  { transform: translate(-14px, -4px); }
                100% { transform: translate(0, 0); }
              }
              @keyframes ss-wm-pulse {
                0%, 100% { opacity: ${streamData.watermark?.opacity ?? 0.4}; }
                50%      { opacity: ${(streamData.watermark?.opacity ?? 0.4) * 0.65}; }
              }
            `}</style>
          </div>
        </Card>
      )}

      {/* V14.5 — clean encoding/failed state instead of Bunny placeholder */}
      {selected && streamData?.not_ready && (
        <Card className="rounded-2xl border-2 border-amber-200" data-testid="course-player-not-ready">
          <CardContent className="aspect-video flex flex-col items-center justify-center gap-3 bg-amber-50/40 p-6 text-center">
            <AlertCircle className="h-10 w-10 text-amber-600" />
            <p className="text-sm font-semibold text-amber-900">
              {streamData.status === 'failed'
                ? 'This episode hit an encoding issue.'
                : 'This episode is still being prepared.'}
            </p>
            <p className="text-xs text-amber-800 max-w-md">
              {streamData.status === 'failed'
                ? "Our team has been notified — we'll have it fixed shortly."
                : (streamData.message || 'Please check back in a few minutes.')}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => playEpisode(selectedEp)}
              data-testid="retry-stream-btn"
            >
              <Loader2 className="h-3.5 w-3.5 mr-1" /> Retry now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* V14.5 — loading skeleton while stream-token in flight */}
      {selected && !streamData && (
        <Card className="rounded-2xl border-2 border-rose-100">
          <CardContent className="aspect-video flex items-center justify-center bg-rose-50/30">
            <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
          </CardContent>
        </Card>
      )}
      </div>

      <style>{`
        @keyframes ss-eq-bounce-1 { 0%, 100% { height: 6px; } 50% { height: 14px; } }
        @keyframes ss-eq-bounce-2 { 0%, 100% { height: 10px; } 50% { height: 4px; } }
        @keyframes ss-eq-bounce-3 { 0%, 100% { height: 14px; } 50% { height: 8px; } }
        .ss-eq-1 { animation: ss-eq-bounce-1 0.8s ease-in-out infinite; }
        .ss-eq-2 { animation: ss-eq-bounce-2 0.8s ease-in-out infinite 0.15s; }
        .ss-eq-3 { animation: ss-eq-bounce-3 0.8s ease-in-out infinite 0.3s; }
      `}</style>

      {/* Episodes */}
      <section>
        <h2 className="text-base font-medium mb-3">Episodes</h2>
        <div className="space-y-2">
          {episodes.map((ep) => (
            <EpisodeRow
              key={ep.id}
              ep={ep}
              isSelected={selected === ep.id}
              onPlay={() => playEpisode(ep)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function EpisodeRow({ ep, onPlay, isSelected }) {
  const access = ep.access || {};
  const allowed = access.allowed;
  const lockCode = access.lock_reason;
  const icon = allowed
    ? <Play className="h-4 w-4" />
    : lockCode === 'LINEAR_LOCKED'
    ? <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
    : lockCode === 'WEEK_LOCKED'
    ? <Baby className="h-4 w-4" />
    : lockCode === 'DRIP_LOCKED' || lockCode === 'DATE_LOCKED'
    ? <CalendarClock className="h-4 w-4" />
    : <Lock className="h-4 w-4" />;

  const countdown = access.unlock_at
    ? `Unlocks ${new Date(access.unlock_at).toLocaleDateString()}`
    : null;

  return (
    <Card
      className={`p-3 flex items-center gap-3 transition-all ${
        allowed
          ? 'hover:bg-rose-50/50 cursor-pointer'
          : 'bg-slate-50/50'
      } ${
        isSelected
          ? 'border-2 border-rose-500 bg-rose-50 shadow-md ring-2 ring-rose-200/60'
          : 'border'
      }`}
      onClick={allowed ? onPlay : undefined}
      data-testid={`ep-row-${ep.id}`}
      data-now-playing={isSelected ? 'true' : 'false'}
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
        isSelected
          ? 'bg-rose-600 text-white'
          : allowed ? 'bg-rose-500 text-white' : 'bg-slate-200 text-slate-500'
      }`}>
        {/* V14.5 — when this row is the active one, swap the static Play icon
            for an animated equalizer so it's visually obvious. */}
        {isSelected ? (
          <span className="flex items-end gap-[2px] h-4" aria-label="Now playing">
            <span className="w-[3px] bg-white rounded-full ss-eq-1" />
            <span className="w-[3px] bg-white rounded-full ss-eq-2" />
            <span className="w-[3px] bg-white rounded-full ss-eq-3" />
          </span>
        ) : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] ${isSelected ? 'text-rose-700 font-semibold' : 'text-muted-foreground'}`}>
            EP {ep.display_order}
          </span>
          <span className={`text-sm truncate ${isSelected ? 'font-semibold text-rose-900' : 'font-medium'}`}>
            {ep.title}
          </span>
          {isSelected && (
            <Badge className="text-[9px] bg-rose-600 text-white border-0 hover:bg-rose-600" data-testid={`now-playing-pill-${ep.id}`}>
              Now playing
            </Badge>
          )}
          {ep.status !== 'ready' && !isSelected && (
            <Badge variant="outline" className="text-[10px]">
              <Clock className="h-2.5 w-2.5 mr-0.5" />{ep.status}
            </Badge>
          )}
        </div>
        {!allowed && access.lock_message && (
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {access.lock_message}
          </p>
        )}
        {countdown && (
          <p className="text-[11px] text-rose-700 mt-0.5">{countdown}</p>
        )}
      </div>
      {/* V14.5 Part 19w — Replace the dead "Notify me" button on every
          locked episode with a tangible upgrade path: a compact "Unlock"
          button that opens WhatsApp. Founder: "Notify me is not needed,
          it doesn't do anything, instead keep a small unlock button which
          takes people to whatsapp." Applies whether or not the course
          itself has a top-level buy CTA. */}
      {!allowed && (
        <EliteUpgradeCard
          size="compact"
          testId={`ep-unlock-${ep.id}`}
        />
      )}
      {ep.duration_min ? (
        <span className={`text-[11px] flex-shrink-0 ml-2 ${isSelected ? 'text-rose-700 font-semibold' : 'text-muted-foreground'}`}>
          {ep.duration_min}m
        </span>
      ) : null}
    </Card>
  );
}
